import React, { useMemo, useRef, useEffect, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

export default function RainSystem({
  isActive = true,
  activateEndTransition = false,
  count = 450,
  area = 10,
  height = 8,
  fallSpeed = 10,
  windX = -0.35,
  opacity = 0.28,
  color = "#cfe8ff",
}) {
  const meshRef = useRef();
  const materialRef = useRef();
  const { camera } = useThree();

  const dummy = useMemo(() => new THREE.Object3D(), []);

  const [shouldRender, setShouldRender] = useState(isActive);

  const visualFadeRef = useRef(isActive ? 1 : 0);
  const targetFadeRef = useRef(isActive ? 1 : 0);

  const effectiveCount = activateEndTransition ? 1200 : count;
  const effectiveFallSpeed = activateEndTransition ? 18 : fallSpeed;
  const effectiveWindX = activateEndTransition ? -1.25 : windX;
  const effectiveOpacity = activateEndTransition ? 0.42 : opacity;

  useEffect(() => {
    if (isActive) {
      setShouldRender(true);
      targetFadeRef.current = 1;
    } else {
      targetFadeRef.current = 0;
    }
  }, [isActive]);

  const drops = useMemo(() => {
    return Array.from({ length: effectiveCount }, () => ({
      x: (Math.random() - 0.5) * area,
      y: Math.random() * height,
      z: (Math.random() - 0.5) * area,
      length: activateEndTransition
        ? THREE.MathUtils.randFloat(0.3, 0.65)
        : THREE.MathUtils.randFloat(0.22, 0.48),
      speed: activateEndTransition
        ? THREE.MathUtils.randFloat(1.1, 1.6)
        : THREE.MathUtils.randFloat(0.85, 1.2),
      drift: activateEndTransition
        ? THREE.MathUtils.randFloat(1.2, 1.8)
        : THREE.MathUtils.randFloat(0.8, 1.2),
      angleOffset: activateEndTransition
        ? THREE.MathUtils.randFloat(-0.12, 0.12)
        : THREE.MathUtils.randFloat(-0.04, 0.04),
    }));
  }, [effectiveCount, area, height, activateEndTransition]);

  useFrame((_, delta) => {
    if (!shouldRender || !meshRef.current) return;

    const fadeSpeed = targetFadeRef.current === 1 ? 0.9 : 0.45;

    visualFadeRef.current = THREE.MathUtils.damp(
      visualFadeRef.current,
      targetFadeRef.current,
      fadeSpeed,
      delta
    );

    const fade = visualFadeRef.current;

    if (materialRef.current) {
      materialRef.current.opacity = effectiveOpacity * fade;
    }

    if (fade < 0.015 && targetFadeRef.current === 0) {
      setShouldRender(false);
      return;
    }

    const cx = camera.position.x;
    const cz = camera.position.z;

    const activeDropCount = Math.floor(effectiveCount * fade);

    for (let i = 0; i < effectiveCount; i++) {
      const d = drops[i];

      if (i >= activeDropCount) {
        dummy.scale.set(0, 0, 0);
        dummy.updateMatrix();
        meshRef.current.setMatrixAt(i, dummy.matrix);
        continue;
      }

      d.y -= effectiveFallSpeed * d.speed * delta;
      d.x += effectiveWindX * d.drift * delta;

      if (d.y < 0) {
        d.y = height;
        d.x = (Math.random() - 0.5) * area;
        d.z = (Math.random() - 0.5) * area;
      }

      if (d.x < -area * 0.75 || d.x > area * 0.75) {
        d.x = (Math.random() - 0.5) * area;
      }

      dummy.position.set(cx + d.x, d.y, cz + d.z);

      const baseAngle = THREE.MathUtils.clamp(
        effectiveWindX * 0.4,
        -0.8,
        0.8
      );

      dummy.rotation.set(0, 0, baseAngle + d.angleOffset);

      dummy.scale.set(
        activateEndTransition ? 0.01 : 0.008,
        d.length,
        1
      );

      dummy.updateMatrix();
      meshRef.current.setMatrixAt(i, dummy.matrix);
    }

    meshRef.current.instanceMatrix.needsUpdate = true;
  });

  if (!shouldRender) return null;

  return (
    <instancedMesh
      ref={meshRef}
      args={[null, null, effectiveCount]}
      frustumCulled={false}
    >
      <planeGeometry args={[1, 1]} />
      <meshBasicMaterial
        ref={materialRef}
        color={color}
        transparent
        opacity={effectiveOpacity}
        depthWrite={false}
        side={THREE.DoubleSide}
        toneMapped={false}
      />
    </instancedMesh>
  );
}